import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image as ImageIcon, Plus, Trash2, GripVertical, Clock, Package, Check, ChevronDown, ChevronRight, Info, AlertTriangle, Leaf, Wheat, Flame, ChefHat } from 'lucide-react';
import { Toggle } from './Toggle';
import { ScheduleModal, Schedule, DEFAULT_SCHEDULE, formatSchedule, isAvailableNow } from './ScheduleModal';
import { TakeawayPackage } from './TakeawayPackagesContext';
import { Modifier } from './ModifiersContext';
import { Category } from './CategoriesContext';
import { KitchenStation } from './BranchesContext';
import { StationIconBadge } from './BranchStationsSection';
export type Variation = {
  id: string;
  name: string;
  price: number;
};
// ... existing code ...
export type NewMenuItem = {
  name: string;
  description: string;
  price: string;
  active: boolean;
  imageUrl?: string;
  variations: Variation[];
  schedule: Schedule;
  packageId?: string;
  modifierIds: string[];
  availableMethods: AvailableMethods;
  optionGroups: OptionGroup[];
  categoryId?: string;
  stationId?: string;
  dietaryTags: string[];
  allergens: string[];
  trackInventory: boolean;
  stockCount?: string;
  lowStockThreshold?: string;
};
const DIETARY_TAGS = [{
  id: 'vegetarian',
  label: 'Vegetarian',
  icon: Leaf,
  color: 'text-emerald-600 bg-emerald-50 border-emerald-200'
}, {
  id: 'vegan',
  label: 'Vegan',
  icon: Leaf,
  color: 'text-green-600 bg-green-50 border-green-200'
}, {
  id: 'gluten-free',
  label: 'Gluten-Free',
  icon: Wheat,
  color: 'text-amber-600 bg-amber-50 border-amber-200'
}, {
  id: 'spicy',
  label: 'Spicy',
  icon: Flame,
  color: 'text-red-600 bg-red-50 border-red-200'
}];
const ALLERGENS = ['Dairy', 'Eggs', 'Peanuts', 'Tree Nuts', 'Soy', 'Wheat', 'Fish', 'Shellfish'];
// Per-item order method availability — toggles which channels the item shows up on.
export type AvailableMethods = {
  dineIn: boolean;
  takeout: boolean;
  delivery: boolean;
  orderAhead: boolean;
};
export const ALL_METHODS_AVAILABLE: AvailableMethods = {
  dineIn: true,
  takeout: true,
  delivery: true,
  orderAhead: true
};
const METHOD_INFO: {
  key: keyof AvailableMethods;
  label: string;
  description: string;
}[] = [{
  key: 'dineIn',
  label: 'Dine-in',
  description: 'Customers ordering from a table QR code.'
}, {
  key: 'takeout',
  label: 'Takeout',
  description: 'Pick up at the counter.'
}, {
  key: 'delivery',
  label: 'Delivery',
  description: 'Sent out via courier.'
}, {
  key: 'orderAhead',
  label: 'Order ahead',
  description: 'Scheduled in advance for dine-in.'
}];
export function AddMenuItemModal({
  onClose,
  onSave,
  initialData,
  packages = [],
  modifiers = [],
  categories = [],
  stations = []
}: {
  onClose: () => void;
  onSave: (item: NewMenuItem) => void;
  initialData?: any;
  packages?: TakeawayPackage[];
  modifiers?: Modifier[];
  categories?: Category[];
  stations?: KitchenStation[];
}) {
  const [activeTab, setActiveTab] = useState<'basic' | 'variations' | 'modifiers' | 'availability' | 'inventory'>('basic');
  const [formData, setFormData] = useState<NewMenuItem>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    price: initialData?.price?.toString() || '',
    active: initialData?.active ?? true,
    imageUrl: initialData?.imageUrl,
    variations: initialData?.variations || [],
    schedule: initialData?.schedule || DEFAULT_SCHEDULE,
    packageId: initialData?.packageId,
    modifierIds: initialData?.modifierIds || [],
    availableMethods: initialData?.availableMethods || ALL_METHODS_AVAILABLE,
    optionGroups: initialData?.optionGroups || [],
    categoryId: initialData?.categoryId || categories[0]?.id,
    stationId: initialData?.stationId,
    dietaryTags: initialData?.dietaryTags || [],
    allergens: initialData?.allergens || [],
    trackInventory: initialData?.stockCount !== undefined,
    stockCount: initialData?.stockCount?.toString() || '',
    lowStockThreshold: initialData?.lowStockThreshold?.toString() || '10'
  });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Handlers (simplified for compilation)
  const handleSave = () => {
    // Convert formData back to appropriate shape before saving
    onSave(formData);
  };
  return <AnimatePresence data-id="element-94">
      <motion.div className="fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50 p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-95">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col" data-id="element-96">
          {/* Tab navigation */}
          <div className="flex border-b" data-id="element-97">
            <button onClick={() => setActiveTab('basic')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'basic' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-98">
              Basic
            </button>
            <button onClick={() => setActiveTab('variations')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'variations' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-99">
              Variations
            </button>
            <button onClick={() => setActiveTab('modifiers')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'modifiers' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-100">
              Modifiers
            </button>
            <button onClick={() => setActiveTab('availability')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'availability' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-101">
              Availability &amp; Routing
            </button>
            <button onClick={() => setActiveTab('inventory')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'inventory' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-102">
              Inventory &amp; Tags
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50" data-id="element-103">
            {activeTab === 'basic' && <div className="space-y-5 max-w-2xl" data-id="element-104">
                {/* Image picker */}
                <div data-id="element-105">
                  <label className="block text-sm font-bold text-gray-900 mb-1.5" data-id="element-106">
                    Item image
                  </label>
                  <div className="flex items-start gap-4" data-id="element-107">
                    <div className="w-24 h-24 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-108">
                      {formData.imageUrl ? <img src={formData.imageUrl} alt="Item preview" className="w-full h-full object-cover" data-id="element-109" /> : <ImageIcon size={28} className="text-gray-400" strokeWidth={1.5} data-id="element-110" />}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-2" data-id="element-111">
                      <label className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer w-fit" data-id="element-112">
                        <ImageIcon size={13} data-id="element-113" />
                        Choose file
                        <input type="file" accept="image/*" className="hidden" onChange={e => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        setFormData(prev => ({
                          ...prev,
                          imageUrl: typeof reader.result === 'string' ? reader.result : undefined
                        }));
                      };
                      reader.readAsDataURL(file);
                    }} data-id="element-114" />
                      </label>
                      <input type="url" value={formData.imageUrl || ''} onChange={e => setFormData({
                    ...formData,
                    imageUrl: e.target.value || undefined
                  })} placeholder="Or paste image URL" className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-xs focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-115" />
                      {formData.imageUrl && <button type="button" onClick={() => setFormData({
                    ...formData,
                    imageUrl: undefined
                  })} className="text-xs font-semibold text-gray-500 hover:text-rose-600 transition-colors self-start" data-id="element-116">
                          Remove image
                        </button>}
                    </div>
                  </div>
                </div>

                <div data-id="element-117">
                  <label className="block text-sm font-bold text-gray-900 mb-1.5" data-id="element-118">
                    Item name
                  </label>
                  <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="e.g. Margherita Pizza" className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-119" />
                </div>

                <div data-id="element-120">
                  <label className="block text-sm font-bold text-gray-900 mb-1.5" data-id="element-121">
                    Description
                  </label>
                  <textarea value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} rows={3} placeholder="What's in this dish? Sells the item." className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 resize-none" data-id="element-122" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-123">
                  <div data-id="element-124">
                    <label className="block text-sm font-bold text-gray-900 mb-1.5" data-id="element-125">
                      Price (ETB)
                    </label>
                    <input type="number" value={formData.price} onChange={e => setFormData({
                  ...formData,
                  price: e.target.value
                })} placeholder="0" min={0} step={0.5} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-126" />
                  </div>
                  <div data-id="element-127">
                    <label className="block text-sm font-bold text-gray-900 mb-1.5" data-id="element-128">
                      Category
                    </label>
                    <select value={formData.categoryId || ''} onChange={e => setFormData({
                  ...formData,
                  categoryId: e.target.value
                })} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-129">
                      {categories.map(c => <option key={c.id} value={c.id} data-id="element-130">
                          {c.name}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-3" data-id="element-131">
                  <div data-id="element-132">
                    <div className="text-sm font-bold text-gray-900" data-id="element-133">
                      Active
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-134">
                      Show this item on the customer menu
                    </div>
                  </div>
                  <Toggle checked={formData.active} onChange={v => setFormData({
                ...formData,
                active: v
              })} data-id="element-135" />
                </div>
              </div>}

            {activeTab === 'variations' && <div className="space-y-4 max-w-2xl" data-id="element-136">
                <div data-id="element-137">
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-138">
                    Variations
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-139">
                    Different sizes or prep styles, each with its own price
                    (e.g. Small / Medium / Large).
                  </p>
                </div>
                {formData.variations.length === 0 && <div className="text-center py-8 px-4 rounded-xl border border-dashed border-gray-300 bg-white" data-id="element-140">
                    <div className="text-sm font-semibold text-gray-700" data-id="element-141">
                      No variations yet
                    </div>
                    <div className="text-xs text-gray-500 mt-1" data-id="element-142">
                      Items without variations use the base price above.
                    </div>
                  </div>}
                <div className="space-y-2" data-id="element-143">
                  {formData.variations.map((v, idx) => <div key={v.id} className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-2" data-id="element-144">
                      <input type="text" value={v.name} onChange={e => {
                  const next = [...formData.variations];
                  next[idx] = {
                    ...v,
                    name: e.target.value
                  };
                  setFormData({
                    ...formData,
                    variations: next
                  });
                }} placeholder="Variation name (e.g. Large)" className="flex-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-145" />
                      <input type="number" value={v.price} onChange={e => {
                  const next = [...formData.variations];
                  next[idx] = {
                    ...v,
                    price: Number(e.target.value) || 0
                  };
                  setFormData({
                    ...formData,
                    variations: next
                  });
                }} placeholder="Price" min={0} step={0.5} className="w-24 px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-146" />
                      <button onClick={() => setFormData({
                  ...formData,
                  variations: formData.variations.filter(x => x.id !== v.id)
                })} className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" title="Remove variation" data-id="element-147">
                        <Trash2 size={14} data-id="element-148" />
                      </button>
                    </div>)}
                </div>
                <button onClick={() => setFormData({
              ...formData,
              variations: [...formData.variations, {
                id: `v_${Date.now().toString(36)}`,
                name: '',
                price: 0
              }]
            })} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-gray-300 text-sm font-semibold text-gray-600 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50/40 transition-colors" data-id="element-149">
                  <Plus size={14} data-id="element-150" />
                  Add variation
                </button>
              </div>}

            {activeTab === 'modifiers' && <div className="space-y-4 max-w-2xl" data-id="element-151">
                <div data-id="element-152">
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-153">Modifiers</h3>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-154">
                    Add-ons customers can attach to this item. Selected
                    modifiers are offered at checkout.
                  </p>
                </div>
                {modifiers.length === 0 ? <div className="text-center py-8 px-4 rounded-xl border border-dashed border-gray-300 bg-white" data-id="element-155">
                    <div className="text-sm font-semibold text-gray-700" data-id="element-156">
                      No modifiers defined
                    </div>
                    <div className="text-xs text-gray-500 mt-1" data-id="element-157">
                      Create modifiers from the Modifiers tab first.
                    </div>
                  </div> : <div className="space-y-2" data-id="element-158">
                    {modifiers.map(m => {
                const checked = formData.modifierIds.includes(m.id);
                return <button key={m.id} type="button" onClick={() => setFormData({
                  ...formData,
                  modifierIds: checked ? formData.modifierIds.filter(id => id !== m.id) : [...formData.modifierIds, m.id]
                })} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${checked ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-159">
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${checked ? 'bg-orange-500 border-2 border-orange-500' : 'border-2 border-gray-300 bg-white'}`} data-id="element-160">
                            {checked && <Check size={12} strokeWidth={3} className="text-white" data-id="element-161" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-162">
                            <div className="text-sm font-semibold text-gray-900 truncate" data-id="element-163">
                              {m.name}
                            </div>
                          </div>
                          <span className={`text-xs font-bold tabular-nums ${m.price > 0 ? 'text-gray-700' : 'text-emerald-600'}`} data-id="element-164">
                            {m.price > 0 ? `+ETB ${m.price.toFixed(2)}` : 'Free'}
                          </span>
                        </button>;
              })}
                  </div>}
              </div>}

            {activeTab === 'availability' && <div className="space-y-6 max-w-2xl" data-id="element-165">
                {/* Availability schedule */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-166">
                  <div className="flex items-start gap-3 mb-3" data-id="element-167">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-168">
                      <Clock size={18} data-id="element-169" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-170">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-171">
                        Availability schedule
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-172">
                        Decide when this item is available. Outside the schedule
                        it stays hidden from customers.
                      </p>
                    </div>
                  </div>

                  {(() => {
                const sched = formData.schedule;
                const alwaysOn = sched.alwaysAvailable;
                const availableNow = alwaysOn || isAvailableNow(sched);
                return <button type="button" onClick={() => setScheduleOpen(true)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 transition-colors text-left" data-id="element-173">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide flex-shrink-0 ${alwaysOn ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : availableNow ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-id="element-174">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${alwaysOn ? 'bg-emerald-500' : availableNow ? 'bg-emerald-500' : 'bg-amber-500'}`} data-id="element-175" />
                          {alwaysOn ? 'Always on' : availableNow ? 'Available now' : 'Outside hours'}
                        </span>
                        <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate" data-id="element-176">
                          {formatSchedule(sched)}
                        </span>
                        <ChevronRight size={16} className="text-gray-400 flex-shrink-0" data-id="element-177" />
                      </button>;
              })()}
                </div>

                {/* Station Routing */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-178">
                  <div className="flex items-start gap-3 mb-4" data-id="element-179">
                    <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0" data-id="element-180">
                      <ChefHat size={18} data-id="element-181" />
                    </div>
                    <div data-id="element-182">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-183">
                        Kitchen Station Routing
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-184">
                        Override the default station for this item. By default,
                        it routes to the station assigned to its category.
                      </p>
                    </div>
                  </div>
                  <select value={formData.stationId || ''} onChange={e => setFormData({
                ...formData,
                stationId: e.target.value || undefined
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-185">
                    <option value="" data-id="element-186">Use Category Default</option>
                    {stations.map(s => <option key={s.id} value={s.id} data-id="element-187">
                        {s.name}
                      </option>)}
                  </select>
                </div>

                {/* Order Methods */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-188">
                  <div className="flex items-start justify-between gap-3 mb-4" data-id="element-189">
                    <div data-id="element-190">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-191">
                        Order methods
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-192">
                        Pick which channels this item is sold through. Disabled
                        methods hide the item from that flow.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-193">
                    {METHOD_INFO.map(m => {
                  const enabled = !!formData.availableMethods?.[m.key];
                  return <div key={m.key} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-gray-200 bg-white" data-id="element-194">
                          <div className="min-w-0" data-id="element-195">
                            <div className="text-sm font-semibold text-gray-900" data-id="element-196">
                              {m.label}
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-197">
                              {m.description}
                            </div>
                          </div>
                          <Toggle checked={enabled} onChange={v => setFormData({
                      ...formData,
                      availableMethods: {
                        ...(formData.availableMethods || ALL_METHODS_AVAILABLE),
                        [m.key]: v
                      }
                    })} data-id="element-198" />
                        </div>;
                })}
                  </div>
                  {formData.availableMethods && Object.values(formData.availableMethods).every(v => !v) && <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200" data-id="element-199">
                        <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-200" />
                        <span className="text-xs text-amber-800" data-id="element-201">
                          With every method off, this item is hidden everywhere.
                          Turn at least one on.
                        </span>
                      </div>}
                </div>

                {/* Takeaway packaging */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-202">
                  <div className="flex items-start gap-3 mb-4" data-id="element-203">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-204">
                      <Package size={18} data-id="element-205" />
                    </div>
                    <div className="flex-1" data-id="element-206">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-207">
                        Takeaway packaging
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-208">
                        Auto-attaches to the cart at checkout for takeout,
                        delivery, and order-ahead — customers never have to
                        choose.
                      </p>
                    </div>
                  </div>

                  {/* Primary toggle: needs packaging? */}
                  {(() => {
                const activePackages = packages.filter(p => p.active);
                const needsPackaging = !!formData.packageId;
                return <Fragment data-id="element-209">
                        <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 mb-3" data-id="element-210">
                          <div className="min-w-0" data-id="element-211">
                            <div className="text-sm font-semibold text-gray-900" data-id="element-212">
                              This item needs packaging
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-213">
                              Turn off for dine-in-only items or anything served
                              on a plate.
                            </div>
                          </div>
                          <Toggle checked={needsPackaging} onChange={v => {
                      if (v) {
                        setFormData({
                          ...formData,
                          packageId: formData.packageId || activePackages[0]?.id
                        });
                      } else {
                        setFormData({
                          ...formData,
                          packageId: undefined
                        });
                      }
                    }} data-id="element-214" />
                        </div>

                        {needsPackaging && activePackages.length > 0 && <div className="space-y-1.5" data-id="element-215">
                            {activePackages.map(p => {
                      const selected = formData.packageId === p.id;
                      return <button key={p.id} type="button" onClick={() => setFormData({
                        ...formData,
                        packageId: p.id
                      })} className={`w-full flex items-start gap-3 text-left px-3 py-3 rounded-lg border transition-colors ${selected ? 'bg-orange-50 border-orange-300 ring-1 ring-orange-200' : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-216">
                                  <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-217">
                                    {selected && <div className="w-2 h-2 rounded-full bg-orange-500" data-id="element-218" />}
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-219">
                                    <div className="flex items-baseline justify-between gap-3" data-id="element-220">
                                      <div className="text-sm font-bold text-gray-900 truncate" data-id="element-221">
                                        {p.name}
                                      </div>
                                      <div className="text-xs font-bold tabular-nums text-emerald-600 flex-shrink-0" data-id="element-222">
                                        +ETB {p.price.toFixed(2)}
                                      </div>
                                    </div>
                                    <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2" data-id="element-223">
                                      {p.description}
                                    </div>
                                  </div>
                                </button>;
                    })}
                            <div className="flex items-center gap-1.5 px-3 pt-2 text-[11px] text-gray-500" data-id="element-224">
                              <Info size={11} data-id="element-225" />1 package is added per quantity
                              of this item ordered.
                            </div>
                          </div>}

                        {needsPackaging && activePackages.length === 0 && <div className="text-xs text-gray-500 text-center py-4 border border-dashed border-gray-300 rounded-lg" data-id="element-226">
                            No active takeaway packages yet. Add some on the
                            Takeaway Packages tab.
                          </div>}
                      </Fragment>;
              })()}
                </div>
              </div>}
            {activeTab === 'inventory' && <div className="space-y-8 max-w-2xl" data-id="element-227">
                {/* Inventory Tracking */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-228">
                  <div className="flex items-center justify-between mb-4" data-id="element-229">
                    <div data-id="element-230">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-231">
                        Inventory Tracking
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-232">
                        Track stock levels and trigger low-stock SMS alerts to
                        assigned staff.
                      </p>
                    </div>
                    <Toggle checked={formData.trackInventory} onChange={v => setFormData({
                  ...formData,
                  trackInventory: v
                })} data-id="element-233" />
                  </div>

                  {formData.trackInventory && <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100" data-id="element-234">
                      <div data-id="element-235">
                        <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-236">
                          Current Stock
                        </label>
                        <input type="number" value={formData.stockCount} onChange={e => setFormData({
                    ...formData,
                    stockCount: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" placeholder="e.g. 50" data-id="element-237" />
                      </div>
                      <div data-id="element-238">
                        <label className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-239">
                          Low Stock Alert Threshold
                        </label>
                        <input type="number" value={formData.lowStockThreshold} onChange={e => setFormData({
                    ...formData,
                    lowStockThreshold: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" placeholder="e.g. 10" data-id="element-240" />
                      </div>
                    </div>}
                </div>

                {/* Dietary Tags */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-241">
                  <h3 className="text-sm font-bold text-gray-900 mb-1" data-id="element-242">
                    Dietary Tags
                  </h3>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-243">
                    Highlight dietary info on the customer menu.
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-244">
                    {DIETARY_TAGS.map(tag => {
                  const active = formData.dietaryTags.includes(tag.id);
                  const Icon = tag.icon;
                  return <button key={tag.id} onClick={() => {
                    const tags = active ? formData.dietaryTags.filter(t => t !== tag.id) : [...formData.dietaryTags, tag.id];
                    setFormData({
                      ...formData,
                      dietaryTags: tags
                    });
                  }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors border ${active ? tag.color : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-245">
                          <Icon size={12} data-id="element-246" />
                          {tag.label}
                        </button>;
                })}
                  </div>
                </div>

                {/* Allergens */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-247">
                  <h3 className="text-sm font-bold text-gray-900 mb-1" data-id="element-248">
                    Allergens
                  </h3>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-249">
                    Select any allergens present in this item.
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-250">
                    {ALLERGENS.map(allergen => {
                  const active = formData.allergens.includes(allergen);
                  return <button key={allergen} onClick={() => {
                    const list = active ? formData.allergens.filter(a => a !== allergen) : [...formData.allergens, allergen];
                    setFormData({
                      ...formData,
                      allergens: list
                    });
                  }} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border ${active ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-251">
                          {allergen}
                        </button>;
                })}
                  </div>
                </div>
              </div>}
          </div>

          {/* Footer */}
          <div className="p-4 border-t flex justify-end space-x-2 bg-white" data-id="element-252">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-253">
              Cancel
            </button>
            <button onClick={handleSave} className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm" data-id="element-254">
              Save item
            </button>
          </div>
        </div>
      </motion.div>
      <ScheduleModal open={scheduleOpen} initial={formData.schedule} itemName={formData.name || 'this item'} onClose={() => setScheduleOpen(false)} onSave={schedule => {
      setFormData(prev => ({
        ...prev,
        schedule
      }));
      setScheduleOpen(false);
    }} data-id="element-255" />
    </AnimatePresence>;
}